import { useEffect, useState } from 'react';
import { isRJChannelPackage } from '@/components/ElectronLayout/ElectronHead/config';

const CHANNEL_PLACEHOLDER = '0';
const DEFAULT_POLL_INTERVAL = 100;
const DEFAULT_TIMEOUT = 1500;

export function isLoginChannelReady(): boolean {
  const channel = localStorage.getItem('channel');
  if (channel === null || channel === '' || channel === CHANNEL_PLACEHOLDER) {
    return false;
  }
  return true;
}

function resolveLoginChannelAsset(defaultAsset: string, rjAsset: string): string {
  return isRJChannelPackage() ? rjAsset : defaultAsset;
}

function getAssetType(asset: string, defaultAsset: string, rjAsset: string): 'rj' | 'default' | 'unknown' {
  if (asset === rjAsset) return 'rj';
  if (asset === defaultAsset) return 'default';
  return 'unknown';
}

function logLoginChannelAsset(scene: string, message: string, payload?: Record<string, unknown>) {
  const tag = `[useLoginChannelLogo:${scene}]`;
  if (payload) {
    console.log(tag, message, payload);
    return;
  }
  console.log(tag, message);
}

interface UseLoginChannelLogoOptions {
  pollInterval?: number;
  timeout?: number;
  scene?: string;
}

export function useLoginChannelLogo(
  defaultAsset: string,
  rjAsset: string,
  options?: UseLoginChannelLogoOptions
): string | null {
  const pollInterval = options?.pollInterval ?? DEFAULT_POLL_INTERVAL;
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
  const scene = options?.scene ?? 'logo';
  const [asset, setAsset] = useState<string | null>(null);

  useEffect(() => {
    let timerId: ReturnType<typeof setInterval> | null = null;
    let stopped = false;
    let lastChannel: string | null = null;
    let lastAsset: string | null = null;
    const startTime = Date.now();

    logLoginChannelAsset(scene, 'mount', {
      channel: localStorage.getItem('channel'),
      channelReady: isLoginChannelReady(),
      isRJChannelPackage: isRJChannelPackage(),
      pollInterval,
      timeout
    });

    const stopPolling = () => {
      stopped = true;
      if (timerId) {
        clearInterval(timerId);
        timerId = null;
      }
    };

    const applyAsset = (nextAsset: string, reason: string, channel: string | null) => {
      if (lastAsset === nextAsset) {
        return;
      }
      lastAsset = nextAsset;
      setAsset(nextAsset);
      logLoginChannelAsset(scene, reason, {
        channel,
        channelReady: isLoginChannelReady(),
        isRJChannelPackage: isRJChannelPackage(),
        assetType: getAssetType(nextAsset, defaultAsset, rjAsset),
        elapsed: Date.now() - startTime
      });
    };

    const tryResolve = () => {
      if (stopped) {
        return;
      }

      const channel = localStorage.getItem('channel');
      const channelReady = isLoginChannelReady();
      const elapsed = Date.now() - startTime;

      if (channel !== lastChannel) {
        logLoginChannelAsset(scene, 'channel changed', {
          from: lastChannel,
          to: channel,
          channelReady,
          isRJChannelPackage: isRJChannelPackage(),
          elapsed
        });
        lastChannel = channel;
      }

      if (channelReady) {
        applyAsset(resolveLoginChannelAsset(defaultAsset, rjAsset), 'asset updated', channel);
      }

      if (elapsed >= timeout) {
        if (channelReady) {
          applyAsset(resolveLoginChannelAsset(defaultAsset, rjAsset), 'resolved by timeout', channel);
        } else {
          applyAsset(defaultAsset, 'resolved by timeout fallback', channel);
        }
        stopPolling();
      } else if (!channelReady) {
        logLoginChannelAsset(scene, 'polling', {
          channel,
          channelReady,
          isRJChannelPackage: isRJChannelPackage(),
          elapsed
        });
      }
    };

    tryResolve();

    if (!stopped) {
      timerId = setInterval(tryResolve, pollInterval);
    }

    return () => {
      stopPolling();
      logLoginChannelAsset(scene, 'unmount', {
        channel: localStorage.getItem('channel'),
        lastAssetType: lastAsset ? getAssetType(lastAsset, defaultAsset, rjAsset) : null
      });
    };
  }, [defaultAsset, rjAsset, pollInterval, timeout, scene]);

  return asset;
}
